import type { $Fetch } from 'ofetch';
import type { AlunoGateway } from '@/application/port/out/AlunoGateway';
import type {
  CreateAlunoCommand,
  ListAlunosQuery,
  UpdateAlunoCommand,
} from '@/application/port/in/dto/AlunoCommand';
import type { Aluno } from '@/domain/model/Aluno';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse, SpringPage } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';
import { DomainError } from '@/domain/error/DomainError';

const ALUNOS_PATH = '/api/v1/alunos';

export class HttpAlunoGateway implements AlunoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(query: ListAlunosQuery): Promise<Page<Aluno>> {
    const params: Record<string, string | number> = {};
    if (query.nome) params['nome'] = query.nome;
    if (query.status) params['status'] = query.status;
    if (typeof query.page === 'number') params['page'] = query.page;
    if (typeof query.size === 'number') params['size'] = query.size;
    if (query.sort) params['sort'] = query.sort;

    const res = await this.client<ApiResponse<SpringPage<Aluno>>>(ALUNOS_PATH, {
      method: 'GET',
      params,
    });
    assertApiSuccess(res);
    return this.toDomainPage(res.data);
  }

  async getById(id: string): Promise<Aluno> {
    const res = await this.client<ApiResponse<Aluno>>(
      `${ALUNOS_PATH}/${encodeURIComponent(id)}`,
      { method: 'GET' },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async create(command: CreateAlunoCommand): Promise<Aluno> {
    const res = await this.client<ApiResponse<Aluno>>(ALUNOS_PATH, {
      method: 'POST',
      body: this.toBody(command),
    });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, command: UpdateAlunoCommand): Promise<Aluno> {
    const res = await this.client<ApiResponse<Aluno>>(
      `${ALUNOS_PATH}/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: this.toBody(command),
      },
    );
    assertApiSuccess(res);
    return res.data;
  }

  private toBody(c: CreateAlunoCommand | UpdateAlunoCommand): Record<string, unknown> {
    return {
      nomeCompleto: c.nomeCompleto,
      nomeSocial: c.nomeSocial,
      naturalidade: c.naturalidade,
      nacionalidade: c.nacionalidade,
      cpf: c.cpf,
      dataNascimento: c.dataNascimento,
      rg: c.rg,
      rgDataEmissao: c.rgDataEmissao,
      rgOrgaoEmissor: c.rgOrgaoEmissor,
      rgUfEmissor: c.rgUfEmissor,
      racaId: c.racaId,
      genero: c.genero,
      estadoCivil: c.estadoCivil,
      necessidadeEspecial: c.necessidadeEspecial,
      necessidadeEspecialTipo: c.necessidadeEspecialTipo,
      profissao: c.profissao,
      empresa: c.empresa,
      nomePai: c.nomePai,
      nomeMae: c.nomeMae,
      cep: c.cep,
      logradouro: c.logradouro,
      numero: c.numero,
      complemento: c.complemento,
      bairro: c.bairro,
      cidade: c.cidade,
      estadoUf: c.estadoUf,
      email: c.email,
      phone: c.phone,
      celular: c.celular,
      poloId: c.poloId,
      possuiGraduacao: c.possuiGraduacao,
      fotoUrl: c.fotoUrl,
    };
  }

  private toDomainPage(p: SpringPage<Aluno>): Page<Aluno> {
    return {
      content: p.content,
      number: p.number,
      size: p.size,
      totalElements: p.totalElements,
      totalPages: p.totalPages,
      first: p.first,
      last: p.last,
    };
  }

  async uploadFoto(alunoId: string, file: File): Promise<Aluno> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await this.client<ApiResponse<Aluno>>(
      `${ALUNOS_PATH}/${encodeURIComponent(alunoId)}/foto`,
      { method: 'POST', body: formData },
    );
    assertApiSuccess(res);
    return res.data;
  }

  async baixarFoto(alunoId: string): Promise<Blob | null> {
    // Backend streama a imagem (proxy, com auth do app); 404 quando não há foto.
    try {
      return await this.client(`${ALUNOS_PATH}/${encodeURIComponent(alunoId)}/foto`, {
        responseType: 'blob',
      });
    } catch (err) {
      if (err instanceof DomainError && err.status === 404) return null;
      throw err;
    }
  }

  // FASE C.6 — verificação manual de contato
  private async toggleVerificado(alunoId: string, path: string, method: 'POST' | 'DELETE'): Promise<Aluno> {
    const res = await this.client<ApiResponse<Aluno>>(
      `${ALUNOS_PATH}/${encodeURIComponent(alunoId)}/${path}/verificar`,
      { method },
    );
    assertApiSuccess(res);
    return res.data;
  }
  verificarEmail(alunoId: string): Promise<Aluno>    { return this.toggleVerificado(alunoId, 'email', 'POST'); }
  desverificarEmail(alunoId: string): Promise<Aluno> { return this.toggleVerificado(alunoId, 'email', 'DELETE'); }
  verificarCelular(alunoId: string): Promise<Aluno>    { return this.toggleVerificado(alunoId, 'celular', 'POST'); }
  desverificarCelular(alunoId: string): Promise<Aluno> { return this.toggleVerificado(alunoId, 'celular', 'DELETE'); }
}
